import { useCallback, useEffect, useState, type Dispatch, type SetStateAction } from "react";
import { Accordion, AccordionDetails, AccordionSummary, TextField } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Grid, Typography } from "@financeable-com-au/financeable-ui";
import * as Yup from "yup";
// @ts-expect-error: ignoring as it is existing js file
import regex from "@/utils/regex";
import { OrganisationAccessComponents } from "@/utils/interface";

export const INITIAL_ACL_INFO = {
  number: "",
  name: "",
  abn: "",
  complaintsEmail: "",
  complaintsPhoneNumber: "",
  complaintsTelephoneNumber: "",
  complaintsAddress: "",
  privacyPolicyLink: "",
  lenders: [] as string[],
};

export const INITIAL_ACL_FIELD_ERRORS = {
  number: "",
  name: "",
  abn: "",
  complaintsEmail: "",
  complaintsPhoneNumber: "",
  complaintsTelephoneNumber: "",
  complaintsAddress: "",
  privacyPolicyLink: "",
};

const optionalAclMatch = (re: RegExp, message: string) =>
  Yup.string()
    .optional()
    .test("match", message, (v) => !v || !String(v).trim() || re.test(String(v).trim()));

const aclFormValidationSchema = Yup.object({
  name: optionalAclMatch(regex.name, "Only letters, spaces and hyphens allowed."),
  number: optionalAclMatch(regex.allowOnlyNumber, "Please enter a valid ACL licence number."),
  abn: optionalAclMatch(regex.ABN, "Not a valid ABN."),
  complaintsEmail: optionalAclMatch(regex.email, "Not a valid email address."),
  complaintsPhoneNumber: optionalAclMatch(regex.phoneNumber, "Not a valid Australia mobile phone number."),
  complaintsTelephoneNumber: optionalAclMatch(regex.telephoneNumber, "Not a valid telephone number."),
  complaintsAddress: optionalAclMatch(regex.addressRegex, "Not a valid address."),
  privacyPolicyLink: Yup.string()
    .optional()
    .test("url", "Please enter a valid privacy policy URL.", (v) => {
      const s = (v ?? "").trim();
      if (!s) return true;
      try {
        // eslint-disable-next-line no-new
        new URL(s);
        return true;
      } catch {
        return false;
      }
    }),
});

/** Non-empty ACL fields only; omit when empty so create/update payloads stay minimal. */
export const getAclPayload = (aclInfo: typeof INITIAL_ACL_INFO): Partial<typeof INITIAL_ACL_INFO> | undefined => {
  if (!aclInfo) return undefined;

  const aclPayload = {
    number: aclInfo.number ?? "",
    name: aclInfo.name ?? "",
    abn: aclInfo.abn ?? "",
    complaintsEmail: aclInfo.complaintsEmail ?? "",
    complaintsPhoneNumber: aclInfo.complaintsPhoneNumber ?? "",
    complaintsTelephoneNumber: aclInfo.complaintsTelephoneNumber ?? "",
    complaintsAddress: aclInfo.complaintsAddress ?? "",
    privacyPolicyLink: aclInfo.privacyPolicyLink,
    lenders: Array.isArray(aclInfo.lenders) ? aclInfo.lenders.slice(0, 6) : [],
  };

  return aclPayload;
};

interface OrganisationAddAclSectionProps {
  aclInfo: typeof INITIAL_ACL_INFO;
  setAclInfo: Dispatch<SetStateAction<typeof INITIAL_ACL_INFO>>;
  aclFieldErrors: typeof INITIAL_ACL_FIELD_ERRORS;
  setAclFieldErrors: Dispatch<SetStateAction<typeof INITIAL_ACL_FIELD_ERRORS>>;
  orgAccessComponent: OrganisationAccessComponents;
}

const OrganisationAddAclSection = ({
  aclInfo,
  setAclInfo,
  aclFieldErrors,
  setAclFieldErrors,
  orgAccessComponent,
}: OrganisationAddAclSectionProps) => {
  const [expanded, setExpanded] = useState(true);
  const [lendersInput, setLendersInput] = useState("");

  const validateField = useCallback(
    async (fieldName: keyof typeof INITIAL_ACL_INFO, value: string) => {
      try {
        await aclFormValidationSchema.validateAt(fieldName, { [fieldName]: value ?? "" });
        setAclFieldErrors((prev) => ({ ...prev, [fieldName]: "" }));
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Validation error";
        setAclFieldErrors((prev) => ({ ...prev, [fieldName]: message }));
      }
    },
    [setAclFieldErrors],
  );

  const onChangeField = async (fieldName: keyof typeof INITIAL_ACL_INFO, value: string) => {
    setAclInfo((prev) => ({ ...prev, [fieldName]: value }));
    await validateField(fieldName, value);
  };

  useEffect(() => {
    const lenders = Array.isArray(aclInfo.lenders) ? aclInfo.lenders : [];
    const normalizedInput = lenders
      .slice(0, 6)
      .map((lender) => lender?.trim())
      .filter(Boolean)
      .join(", ");
    setLendersInput(normalizedInput);
  }, [aclInfo.lenders]);

  const onBlurTopLenders = () => {
    const raw = lendersInput.trim();
    const lenders = raw
      ? raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 6)
      : [];
    setLendersInput(lenders.join(", "));
    setAclInfo((prev) => ({ ...prev, lenders }));
  };

  return (
    <Grid xs={12}>
      <Accordion
        expanded={expanded}
        onChange={(_, next) => setExpanded(next)}
        disableGutters
        sx={{
          width: "100%",
          "&:before": { display: "none" },
          boxShadow: "none",
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="org-acl-panel" id="org-acl-header">
          <Typography level="title-sm" style={{ fontWeight: 600 }}>
            ACL (Australian Credit Licence) — optional
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid xs={12} sm={6}>
              <TextField
                fullWidth
                name="aclNumber"
                label="ACL credit licence number"
                variant="filled"
                size="small"
                value={aclInfo.number ?? ""}
                onChange={(e) => onChangeField("number", e.target.value)}
                margin="dense"
                helperText={aclFieldErrors.number}
                error={!!aclFieldErrors.number}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                fullWidth
                name="aclName"
                label="ACL name"
                variant="filled"
                size="small"
                value={aclInfo.name ?? ""}
                onChange={(e) => onChangeField("name", e.target.value)}
                margin="dense"
                helperText={aclFieldErrors.name}
                error={!!aclFieldErrors.name}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid xs={12} sm={6}>
              <TextField
                fullWidth
                name="aclAbn"
                label="ACL ABN"
                variant="filled"
                size="small"
                value={aclInfo.abn ?? ""}
                onChange={(e) => onChangeField("abn", e.target.value)}
                margin="dense"
                helperText={aclFieldErrors.abn}
                error={!!aclFieldErrors.abn}
                inputProps={{ maxLength: 50 }}
              />
            </Grid>
            {orgAccessComponent.isAclComplaintsEmail && (
              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="aclComplaintsEmail"
                  label="ACL complaints email"
                  type="email"
                  variant="filled"
                  size="small"
                  value={aclInfo.complaintsEmail ?? ""}
                  onChange={(e) => onChangeField("complaintsEmail", e.target.value)}
                  margin="dense"
                  helperText={aclFieldErrors.complaintsEmail}
                  error={!!aclFieldErrors.complaintsEmail}
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
            )}
            {orgAccessComponent.isAclComplaintsPhoneNumber && (
              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="text"
                  name="aclComplaintsPhoneNumber"
                  label="ACL complaints mobile number"
                  variant="filled"
                  size="small"
                  value={aclInfo.complaintsPhoneNumber ?? ""}
                  onChange={(e) => onChangeField("complaintsPhoneNumber", e.target.value)}
                  margin="dense"
                  helperText={aclFieldErrors.complaintsPhoneNumber}
                  error={!!aclFieldErrors.complaintsPhoneNumber}
                  inputProps={{ maxLength: 12 }}
                  placeholder="0400123123"
                />
              </Grid>
            )}
            {orgAccessComponent.isAclComplaintsTelephoneNumber && (
              <Grid xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="text"
                  name="aclComplaintsTelephoneNumber"
                  label="ACL complaints telephone number"
                  variant="filled"
                  size="small"
                  value={aclInfo.complaintsTelephoneNumber ?? ""}
                  onChange={(e) => onChangeField("complaintsTelephoneNumber", e.target.value)}
                  margin="dense"
                  helperText={aclFieldErrors.complaintsTelephoneNumber}
                  error={!!aclFieldErrors.complaintsTelephoneNumber}
                  inputProps={{ maxLength: 12 }}
                  placeholder="0000000000"
                />
              </Grid>
            )}
            <Grid xs={12}>
              <TextField
                fullWidth
                name="aclComplaintsAddress"
                label="ACL complaints business address"
                variant="filled"
                size="small"
                value={aclInfo.complaintsAddress ?? ""}
                onChange={(e) => onChangeField("complaintsAddress", e.target.value)}
                margin="dense"
                helperText={aclFieldErrors.complaintsAddress}
                error={!!aclFieldErrors.complaintsAddress}
                inputProps={{ maxLength: 200 }}
              />
            </Grid>
            <Grid xs={12}>
              <TextField
                fullWidth
                name="aclPrivacyPolicyLink"
                label="ACL privacy policy link"
                variant="filled"
                size="small"
                value={aclInfo.privacyPolicyLink ?? ""}
                onChange={(e) => onChangeField("privacyPolicyLink", e.target.value)}
                margin="dense"
                helperText={aclFieldErrors.privacyPolicyLink}
                error={!!aclFieldErrors.privacyPolicyLink}
                inputProps={{ maxLength: 500 }}
                placeholder="https://"
              />
            </Grid>
            {orgAccessComponent.isShowTopLenders && (
              <Grid xs={12}>
                <TextField
                  fullWidth
                  label="ACL Top 6 Lenders by Volume"
                  placeholder="e.g. ANZ, Azora, etc..."
                  variant="filled"
                  type="text"
                  value={lendersInput}
                  onChange={(e) => setLendersInput(e.target.value)}
                  onBlur={onBlurTopLenders}
                  margin="dense"
                  size="small"
                  helperText="Enter up to 6 lenders separated by commas"
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default OrganisationAddAclSection;
